import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MoonLoader } from "react-spinners";
import { useTranslate } from "../../../../hooks/LanguageProvider";
import { StatsComponentProps } from "../../../../type";

interface StatsData {
  maxSpeed: number;
  avgSpeed: number;
  movingCount: number;
  totalVehicles: number;
  buckets: number[];
}

type Dash2StatsProps = StatsComponentProps & {
  periodHours?: number;
  title?: string;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;

function numberOrZero(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function speedBuckets(speeds: number[]) {
  return [
    speeds.filter((speed) => speed <= 0).length,
    speeds.filter((speed) => speed > 0 && speed <= 30).length,
    speeds.filter((speed) => speed > 30 && speed <= 60).length,
    speeds.filter((speed) => speed > 60 && speed <= 90).length,
    speeds.filter((speed) => speed > 90).length,
  ];
}

const StatsComponent: React.FC<Dash2StatsProps> = ({ title }) => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { translate } = useTranslate();

  const fetchData = async () => {
    if (!backendUrl) return;
    const userId = localStorage.getItem("userid");
    if (!userId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/map/find/${userId}`, { mode: "cors" });
      if (!response.ok) throw new Error(`Map API ${response.status}`);
      const vehicles = await response.json();
      const list = Array.isArray(vehicles) ? vehicles : [];
      const speeds = list.map((vehicle: any) => numberOrZero(vehicle?.SOG)).filter((speed: number) => speed >= 0);
      const movingSpeeds = speeds.filter((speed: number) => speed > 5);
      const maxSpeed = speeds.length ? Math.max(...speeds) : 0;
      const avgSpeed = movingSpeeds.length
        ? movingSpeeds.reduce((sum: number, speed: number) => sum + speed, 0) / movingSpeeds.length
        : 0;

      setStats({
        maxSpeed,
        avgSpeed,
        movingCount: movingSpeeds.length,
        totalVehicles: list.length,
        buckets: speedBuckets(speeds),
      });
    } catch (error: any) {
      setError(error.message || "Erreur API map");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = window.setInterval(fetchData, 60000);
    return () => window.clearInterval(interval);
  }, []);

  const maxSpeed = numberOrZero(stats?.maxSpeed);
  const avgSpeed = numberOrZero(stats?.avgSpeed);
  const movingCount = numberOrZero(stats?.movingCount);

  const speedCategories = ["0", "1-30", "31-60", "61-90", "90+"];
  const lineOptions: Highcharts.Options = {
    chart: {
      type: "areaspline",
      height: 260,
      backgroundColor: "transparent",
      spacing: [8, 0, 0, 0],
    },
    credits: { enabled: false },
    title: { text: undefined },
    xAxis: {
      categories: speedCategories,
      title: { text: undefined },
      labels: { style: { color: "#64748b", fontWeight: "600" } },
      lineColor: "#e5e7eb",
      tickColor: "#e5e7eb",
    },
    yAxis: {
      min: 0,
      allowDecimals: false,
      title: { text: translate("Vehicles") },
      gridLineColor: "#eef2f7",
    },
    legend: { enabled: false },
    tooltip: {
      headerFormat: "<b>{point.key} km/h</b><br/>",
      pointFormat: "<b>{point.y}</b> vehicules",
    },
    plotOptions: {
      areaspline: {
        marker: { enabled: false },
        fillOpacity: 0.18,
        lineWidth: 3,
        color: "#2563eb",
      },
    },
    series: [{
      type: "areaspline",
      name: translate("Vitesse"),
      data: stats?.buckets || [],
    }],
  };

  return (
    <div className="dash2-chart-widget dash2-speed-widget">
      <div className="dash2-chart-header">
        <div>
          <span className="dash2-chart-kicker">Map temps reel</span>
          <h3>{title || translate("Speedometer")}</h3>
        </div>
        <button type="button" className="dash2-chart-refresh" onClick={fetchData} title={translate("Refresh")}>
          <i className="las la-sync" />
        </button>
      </div>

      {loading && (
        <div className="dash2-chart-loader">
          <MoonLoader size={34} color="#2563eb" loading={loading} />
        </div>
      )}

      {error && <div className="dash2-chart-empty">{error}</div>}

      {!loading && !error && stats && (
        <>
          <div className="dash2-chart-metrics">
            <span><strong>{maxSpeed.toFixed(1)}</strong>{translate("Max")} km/h</span>
            <span><strong>{avgSpeed.toFixed(1)}</strong>{translate("Avg")} km/h</span>
            <span><strong>{movingCount}</strong>{translate("Driving")}</span>
          </div>
          <HighchartsReact highcharts={Highcharts} options={lineOptions} />
        </>
      )}
    </div>
  );
};

export default StatsComponent;
